import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Stack} from '@/components/shared/Stack'
import {useNavigate} from 'react-router-dom'
import {Database} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {Card, CardTitle, CardDescription} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Badge} from '@/components/ui/badge'
import {PageLayout} from '@/components/shared/PageLayout'
import {routes} from '@/lib/routes'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {useServices} from '@/services/context'
import {CONCEPT_ICON} from '@/lib/concepts'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {TypeControllerTypeTablesEndpoint} from '@/types/endpoint-contracts'

export function Types() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.types.pageTitle)
    const navigate = useNavigate()
    const {hasPermission} = useAuth()

    const canView = hasPermission(permissions.types.view)

    const {
        queryString,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: TypeControllerTypeTablesEndpoint,
        defaultSortBy: '',
    })

    const {data: typeTables = [], isLoading, error} = useQuery({
        queryKey: ['types', queryString],
        queryFn: () => services.types.list(Object.fromEntries(new URLSearchParams(queryString))),
        enabled: canView,
    })

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    if (!canView) {
        return (
            <MutedText>{i18n.types.noPermission}</MutedText>
        )
    }

    return (
        <PageLayout
            data-testid="types-page"
            variant="list"
            icon={CONCEPT_ICON.typeValue}
            title={i18n.types.typesTitle}
            description={i18n.types.typesSubtitle}
            count={typeTables.length}
            loading={isLoading}
            error={error}
            filters={
                <UnifiedSearchBar
                    contract={TypeControllerTypeTablesEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.types.searchPlaceholder}
                />
            }
        >
            <Stack gap="md">
                {[...typeTables].sort((a, b) => a.displayName.localeCompare(b.displayName)).map((table) => (
                    <Card
                        key={table.tableName}
                        className="cursor-pointer hover:bg-accent/50"
                        onClick={() => navigate(routes.schema.types.detail(table.tableName))}
                    >
                        <RowCardHeader className="pb-2">
                            <InlineRow gap="lg">
                                <Icon icon={Database} size="lg" muted/>
                                <Stack gap="xs">
                                    <CardTitle data-testid={`card-title-type-${table.tableName}`} className="text-lg">{table.displayName}</CardTitle>
                                    <CardDescription>{table.tableName}</CardDescription>
                                </Stack>
                            </InlineRow>
                            <InlineRow gap="lg">
                                {table.entityType && (
                                    <Badge variant="default">
                                        {i18n.types.entityTypeBadge}
                                    </Badge>
                                )}
                                <Badge variant="secondary">
                                    {i18n.types.valueCount({count: table.count})}
                                </Badge>
                                {table.activeCount < table.count && (
                                    <Badge variant="outline">
                                        {i18n.types.activeCount({count: table.activeCount})}
                                    </Badge>
                                )}
                            </InlineRow>
                        </RowCardHeader>
                    </Card>
                ))}
                {!isLoading && typeTables.length === 0 && (
                    <MutedText block centered>
                        {hasActiveFilters ? i18n.types.noTablesMatch : i18n.types.noTablesFound}
                    </MutedText>
                )}
            </Stack>
        </PageLayout>
    )
}
